import React, { useState, useRef, useEffect } from "react";
import CommonTable from "../../components/commonComponent/CommanTable";
import { FaEye, FaEllipsisV, FaEdit, FaTrashAlt } from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";
import axios from "../../utils/axiosInstance";
import { toastError } from "../../utils/alertHelper";
import { confirmDelete } from "../../utils/alertHelper";
import { hasPermission } from "../../utils/permissionHelper";

const allFields = [
  { key: "srNo", label: "Sr.No." },
  { key: "itemCode", label: "Item Code" },
  { key: "itemName", label: "Item Name" },
  { key: "rate", label: "Rate(₹)" },
];

const forcedFields = ["srNo"];

function ItemIndex() {
  const [visibleFields, setVisibleFields] = useState(allFields.map(f => f.key));
  const [showColumnDropdown, setShowColumnDropdown] = useState(false);
  const columnDropdownRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [actionMenuOpen, setActionMenuOpen] = useState(null);
  const actionMenuRef = useRef(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const defaultLimit = process.env.REACT_APP_PAGE_LIMIT ? parseInt(process.env.REACT_APP_PAGE_LIMIT, 10) : 10;
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(defaultLimit);
  const [totalItemsCount, setTotalItemsCount] = useState(0);

  // User permissions
  const [user, setUser] = useState(null);
  useEffect(() => {
    try {
      const stored = localStorage.getItem("user");
      if (stored) setUser(JSON.parse(stored));
    } catch {}
  }, []);

  // Item permissions
  const canListItem = hasPermission(user, "Item", "List");
  const canViewItem = hasPermission(user, "Item", "View");
  const canCreateItem = hasPermission(user, "Item", "Create");
  const canEditItem = hasPermission(user, "Item", "Edit");
  const canDeleteItem = hasPermission(user, "Item", "Delete");

  // Fetch items from API
  const fetchItems = async () => {
    if (!canListItem && !canViewItem) {
      setItems([]);
      return;
    }
    try {
      setLoading(true);
      const businessId = localStorage.getItem("businessId");
      const page = 1;
      const limit = 10; // Increase limit to get all items
      const res = await axios.get(
        `/api/item?page=${page}&limit=${limit}&business=${businessId}`
      );
      if (res.data && Array.isArray(res.data.items)) {
        setItems(res.data.items);
      }
    } catch (err) {
      console.error("Failed to fetch items:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, [canListItem, canViewItem]);

  // Filtered items based on search value
  const filteredItems = items.filter(
    (item) =>
      (item.name && item.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (item.code && item.code.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Table columns
  const tableColumns = allFields
    .filter(
      (field) =>
        forcedFields.includes(field.key) || visibleFields.includes(field.key)
    )
    .map((field) => ({ key: field.key, label: field.label }))
    .concat([{ key: "actions", label: "Action" }]);

  // Delete handler
  const handleDelete = async (item) => {
    if (!canDeleteItem) {
      toastError("You do not have permission to delete items.");
      return;
    }
    try {
      const res = await confirmDelete(`Are you sure you want to delete ${item.name || item.itemName}?`);
      if (!res.isConfirmed) return;
    } catch (err) {
      return;
    }
    try {
      await axios.delete(`/api/item/${item._id}`);
      // Refresh the list after deletion
      fetchItems();
    } catch (err) {
      console.error("Failed to delete item:", err);
      toastError("Failed to delete item. Please try again.");
    }
  };

  // Table data
  const tableData = filteredItems.map((item, idx) => {
    let rate = "";
    if (Array.isArray(item.variants) && item.variants.length > 0) {
      rate = item.variants[0].rate != null ? item.variants[0].rate : "";
    }
    return {
      ...item,
      srNo: (page - 1) * limit + idx + 1,
      itemCode: item.code || "",
      itemName: item.name || "",
      rate: rate,
      actions: (
        <div className="d-flex align-items-center" style={{ position: "relative" }}>
          {canViewItem && (
            <Link
              to={`/user/item/view/${item._id}`}
              className="btn btn-sm py-0 px-1 fs-6 lh-sm shadow-none border-0 text-primary-custom"
              title="View"
              style={{ textDecoration: "none" }}
            >
              <FaEye size={14} />
            </Link>
          )}
          <button
            className="btn btn-sm d-flex align-items-center ms-1 shadow-none border-0 bg-transparent"
            title="Actions"
            onClick={(e) => {
              e.stopPropagation();
              setActionMenuOpen(actionMenuOpen === item._id ? null : item._id);
            }}
            style={{ gap: "4px" }}
          >
            <FaEllipsisV size={14} />
          </button>
          {actionMenuOpen === item._id && (
            <div
              ref={actionMenuRef}
              style={{
                position: "fixed",
                zIndex: 1050,
                background: "#fff",
                border: "1px solid #ddd",
                borderRadius: 6,
                boxShadow: "0 2px 10px rgba(0,0,0,0.15)",
                minWidth: 150,
                padding: "6px 0",
              }}
            >
              {canEditItem && (
                <button
                  className="btn btn-sm w-100 text-start py-1 px-2 lh-sm shadow-none border-0 text-primary-custom d-flex align-items-center"
                  onClick={() => navigate(`/user/item/edit/${item._id}`)}
                  style={{ gap: "6px" }}
                >
                  <FaEdit size={14} /> Edit
                </button>
              )}
              {canDeleteItem && (
                <button
                  className="btn btn-sm w-100 text-start py-1 px-2 lh-sm shadow-none border-0 text-danger d-flex align-items-center"
                  onClick={() => handleDelete(item)}
                  style={{ gap: "6px" }}
                >
                  <FaTrashAlt size={14} /> Delete
                </button>
              )}
            </div>
          )}
        </div>
      ),
    };
  });

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        actionMenuRef.current &&
        !actionMenuRef.current.contains(event.target)
      ) {
        setActionMenuOpen(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!showColumnDropdown) return;
    const handleClickOutside = (event) => {
      if (
        columnDropdownRef.current &&
        !columnDropdownRef.current.contains(event.target)
      ) {
        setShowColumnDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () =>
      document.removeEventListener("mousedown", handleClickOutside);
  }, [showColumnDropdown]);

  return (
    <div className="container-fluid mt-4">
      <div className="d-flex justify-content-between align-items-center mb-4 mt-4">
        <h2 className="fw-bold">Items</h2>
        <div className="d-flex gap-2 align-items-center">
          {canCreateItem && (
            <button
              className="btn btn-primary"
              onClick={() => navigate("/user/item/create")}
            >
              <i className="fas fa-plus me-1"></i> Add New Item
            </button>
          )}
        </div>
      </div>
      <div className="row mb-4">
        <div className="col-md-6"></div>
        <div className="col-md-6 d-flex justify-content-end">
          <div className="input-group" style={{ maxWidth: 280, borderRadius: "6px", overflow: "hidden" }}>
            <span className="input-group-text bg-white border-end-0" style={{ borderRadius: "6px 0 0 6px" }}>
              <i className="fas fa-search text-muted"></i>
            </span>
            <input
              type="text"
              className="form-control border-start-0"
              placeholder="Type to search..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              style={{ boxShadow: "none", borderRadius: "0 6px 6px 0" }}
            />
          </div>
        </div>
      </div>
      <CommonTable
        columns={tableColumns}
        data={tableData}
        itemsPerPageOptions={[10, 15, 25, 50]}
        defaultItemsPerPage={15}
        page={page}
        onPageChange={setPage}
        itemsPerPage={limit}
        onItemsPerPageChange={setLimit}
        totalItems={totalItemsCount}
      />
    </div>
  );
}

export default ItemIndex;